import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "api/axios";
import useAuth from "hooks/useAuth";
import {
  CONTEXT_MODULES_BY_SLUG,
  buildContextPath,
  getDefaultModuleForLevel,
  isModuleAllowedForLevel,
} from "config/contextModules";
import {
  getExplicitDashboardRoleScope,
  isSuperuserUser,
} from "utils/dashboardScope";
import { getActiveAssessmentUsers } from "service/assessment-users.service";

const LocationContext = createContext(null);

const GHANA_ROOT = {
  id: "ghana",
  slug: "ghana",
  name: "Ghana",
  level: "national",
  children: [],
};

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function sortByName(a, b) {
  return a.name.localeCompare(b.name);
}

function normalizeMatch(value) {
  return String(value || "").trim().toLowerCase();
}

function getAssignedRegionIds(user) {
  return new Set(
    [
      ...(user?.user?.regions || []).map((region) => region?.id),
      ...(user?.user?.organisationUnits || [])
        .filter((unit) => Number(unit?.level) === 2)
        .map((unit) => unit?.id),
      ...(user?.user?.dataViewOrganisationUnits || [])
        .filter((unit) => Number(unit?.level) === 2)
        .map((unit) => unit?.id),
    ].filter(Boolean),
  );
}

function getAssignedRegionNames(user) {
  return new Set(
    [
      ...(user?.user?.regions || []).map((region) => region?.displayName || region?.name),
      ...(user?.user?.organisationUnits || [])
        .filter((unit) => Number(unit?.level) === 2)
        .map((unit) => unit?.displayName || unit?.name),
      ...(user?.user?.dataViewOrganisationUnits || [])
        .filter((unit) => Number(unit?.level) === 2)
        .map((unit) => unit?.displayName || unit?.name),
    ]
      .map((value) => normalizeMatch(value))
      .filter(Boolean),
  );
}

function getAssignedDistrictIds(user) {
  return new Set(
    [
      ...(user?.user?.districts || []).map((district) => district?.id),
      ...(user?.user?.organisationUnits || [])
        .filter((unit) => Number(unit?.level) === 3)
        .map((unit) => unit?.id),
      ...(user?.user?.dataViewOrganisationUnits || [])
        .filter((unit) => Number(unit?.level) === 3)
        .map((unit) => unit?.id),
      ...(user?.user?.communities || []).map((community) => community?.parent?.id),
      ...(user?.user?.organisationUnits || [])
        .filter((unit) => Number(unit?.level) === 4)
        .map((unit) => unit?.parent?.id),
    ].filter(Boolean),
  );
}

function shouldRestrictOrgTree({
  explicitScope,
  hasRegionAssignments,
  hasDistrictAssignments,
  isSuperuser,
}) {
  if (isSuperuser || explicitScope === "national") {
    return false;
  }

  if (explicitScope === "regional" || explicitScope === "district") {
    return true;
  }

  return hasRegionAssignments || hasDistrictAssignments;
}

function buildOrgTreeFromBackend(districtRecords, subdistrictRecords, user) {
  const accessibleRegionIds = getAssignedRegionIds(user);
  const accessibleRegionNames = getAssignedRegionNames(user);
  const accessibleDistrictIds = getAssignedDistrictIds(user);
  const explicitScope = getExplicitDashboardRoleScope(user);
  const isSuperuser = isSuperuserUser(user);
  const hasRegionAssignments = accessibleRegionIds.size > 0 || accessibleRegionNames.size > 0;
  const hasDistrictAssignments = accessibleDistrictIds.size > 0;
  const restrictOrgTree = shouldRestrictOrgTree({
    explicitScope,
    hasRegionAssignments,
    hasDistrictAssignments,
    isSuperuser,
  });
  const hasNationalAccess = !restrictOrgTree;

  // Index subdistricts by their parent district ID for O(1) lookup
  const subdistrictsByDistrictId = new Map();
  (subdistrictRecords || []).forEach((sub) => {
    if (!sub.districtId) return;
    if (!subdistrictsByDistrictId.has(sub.districtId)) {
      subdistrictsByDistrictId.set(sub.districtId, []);
    }
    subdistrictsByDistrictId.get(sub.districtId).push(sub);
  });

  const regionMap = new Map();

  (districtRecords || []).forEach((district) => {
    const regionNameKey = normalizeMatch(district.regionName);
    const regionGranted =
      accessibleRegionIds.has(district.regionId) || accessibleRegionNames.has(regionNameKey);
    const districtGranted = accessibleDistrictIds.has(district.id);
    const hasAccess = !restrictOrgTree || regionGranted || districtGranted;

    if (!hasAccess) {
      return;
    }

    const regionId = district.regionId;
    const regionName = district.regionName || "Unknown Region";
    const regionSlug = slugify(regionName);
    const districtSlug = slugify(district.name);

    if (!regionMap.has(regionId)) {
      regionMap.set(regionId, {
        id: `region:${regionSlug}`,
        orgUnitId: regionId || null,
        slug: regionSlug,
        name: regionName,
        level: "region",
        regionName,
        hasAccess: !restrictOrgTree || regionGranted,
        children: [],
      });
    }

    const subdistricts = (subdistrictsByDistrictId.get(district.id) || [])
      .map((sub) => ({
        id: `subdistrict:${regionSlug}:${districtSlug}:${slugify(sub.name)}`,
        orgUnitId: sub.id,
        slug: slugify(sub.name),
        name: sub.name,
        level: "subdistrict",
        regionName,
        regionSlug,
        districtName: district.name,
        districtSlug,
        parentId: `district:${regionSlug}:${districtSlug}`,
        parentRegionId: `region:${regionSlug}`,
        parentRegionOrgUnitId: regionId || null,
        hasAccess,
      }))
      .sort(sortByName);

    regionMap.get(regionId).children.push({
      id: `district:${regionSlug}:${districtSlug}`,
      orgUnitId: district.id,
      slug: districtSlug,
      name: district.name,
      level: "district",
      regionName,
      regionSlug,
      regionOrgUnitId: regionId || null,
      districtName: district.name,
      parentId: `region:${regionSlug}`,
      hasAccess,
      children: subdistricts,
    });
  });

  const regions = Array.from(regionMap.values())
    .map((region) => ({ ...region, children: [...region.children].sort(sortByName) }))
    .sort(sortByName);

  return { ...GHANA_ROOT, hasAccess: hasNationalAccess, children: regions };
}

function buildFallbackTree(user) {
  const explicitScope = getExplicitDashboardRoleScope(user);
  const isSuperuser = isSuperuserUser(user);
  const hasRegionAssignments = getAssignedRegionIds(user).size > 0 || getAssignedRegionNames(user).size > 0;
  const hasDistrictAssignments = getAssignedDistrictIds(user).size > 0;
  const restrictOrgTree = shouldRestrictOrgTree({
    explicitScope,
    hasRegionAssignments,
    hasDistrictAssignments,
    isSuperuser,
  });

  return { ...GHANA_ROOT, hasAccess: !restrictOrgTree };
}

function getHighestAccessibleLocation(orgTree) {
  if (!orgTree) return null;
  if (orgTree.hasAccess !== false) return orgTree;

  const regions = orgTree.children || [];
  const accessibleRegion = regions.find((region) => region.hasAccess !== false);
  if (accessibleRegion) return accessibleRegion;

  for (const region of regions) {
    const accessibleDistrict = (region.children || []).find(
      (district) => district.hasAccess !== false,
    );
    if (accessibleDistrict) return accessibleDistrict;
  }

  for (const region of regions) {
    for (const district of region.children || []) {
      const accessibleSubdistrict = (district.children || []).find(
        (subdistrict) => subdistrict.hasAccess !== false,
      );
      if (accessibleSubdistrict) return accessibleSubdistrict;
    }
  }

  return null;
}

function parseContextPath(pathname) {
  if (!pathname?.startsWith("/ghana")) {
    return {
      isContextRoute: false,
      regionSlug: null,
      districtSlug: null,
      subdistrictSlug: null,
      moduleSlug: null,
      rawSegments: [],
    };
  }

  const rawSegments = pathname
    .replace(/^\/ghana\/?/, "")
    .split("/")
    .filter(Boolean);

  if (rawSegments.length === 0) {
    return {
      isContextRoute: true,
      regionSlug: null,
      districtSlug: null,
      subdistrictSlug: null,
      moduleSlug: null,
      rawSegments,
    };
  }

  if (rawSegments.length === 1) {
    return {
      isContextRoute: true,
      regionSlug: null,
      districtSlug: null,
      subdistrictSlug: null,
      moduleSlug: rawSegments[0],
      rawSegments,
    };
  }

  if (rawSegments.length === 2) {
    return {
      isContextRoute: true,
      regionSlug: rawSegments[0],
      districtSlug: null,
      subdistrictSlug: null,
      moduleSlug: rawSegments[1],
      rawSegments,
    };
  }

  if (rawSegments.length === 3) {
    return {
      isContextRoute: true,
      regionSlug: rawSegments[0],
      districtSlug: rawSegments[1],
      subdistrictSlug: null,
      moduleSlug: rawSegments[2],
      rawSegments,
    };
  }

  return {
    isContextRoute: true,
    regionSlug: rawSegments[0],
    districtSlug: rawSegments[1],
    subdistrictSlug: rawSegments[2],
    moduleSlug: rawSegments[3],
    rawSegments,
  };
}

function findRegionBySlug(orgTree, regionSlug) {
  if (!orgTree || !regionSlug) return null;
  return orgTree.children?.find((region) => region.slug === regionSlug) || null;
}

function findDistrictBySlug(regionNode, districtSlug) {
  if (!regionNode || !districtSlug) return null;
  return regionNode.children?.find((district) => district.slug === districtSlug) || null;
}

function findSubdistrictBySlug(districtNode, subdistrictSlug) {
  if (!districtNode || !subdistrictSlug) return null;
  return districtNode.children?.find((subdistrict) => subdistrict.slug === subdistrictSlug) || null;
}

function buildActivePathIds(currentLocation) {
  if (!currentLocation) return [GHANA_ROOT.id];
  if (currentLocation.level === "subdistrict") {
    return [
      GHANA_ROOT.id,
      currentLocation.parentRegionId || `region:${currentLocation.regionSlug}`,
      currentLocation.parentId,
      currentLocation.id,
    ].filter(Boolean);
  }
  if (currentLocation.level === "district") {
    return [GHANA_ROOT.id, currentLocation.parentId, currentLocation.id].filter(Boolean);
  }
  if (currentLocation.level === "region") {
    return [GHANA_ROOT.id, currentLocation.id];
  }
  return [GHANA_ROOT.id];
}

export function LocationProvider({ children }) {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { user } = useAuth();

  const [orgTree, setOrgTree] = useState(() => buildFallbackTree(user));
  const [isOrgTreeLoading, setIsOrgTreeLoading] = useState(true);
  const [orgTreeError, setOrgTreeError] = useState("");
  const [assessmentUsers, setAssessmentUsers] = useState([]);
  const [isAssessmentUsersLoading, setIsAssessmentUsersLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadAssessmentUsers = async () => {
      setIsAssessmentUsersLoading(true);
      try {
        const users = await getActiveAssessmentUsers();
        if (isMounted) {
          setAssessmentUsers(users);
        }
      } catch (error) {
        console.error("Failed to load assessment users:", error);
        if (isMounted) {
          setAssessmentUsers([]);
        }
      } finally {
        if (isMounted) {
          setIsAssessmentUsersLoading(false);
        }
      }
    };

    loadAssessmentUsers();

    return () => {
      isMounted = false;
    };
  }, []);

  const routeState = useMemo(
    () => parseContextPath(routerLocation.pathname),
    [routerLocation.pathname],
  );

  useEffect(() => {
    let isMounted = true;

    const loadOrgUnits = async () => {
      if (!user?.user) {
        if (isMounted) {
          setOrgTree(buildFallbackTree(user));
          setIsOrgTreeLoading(false);
          setOrgTreeError("");
        }
        return;
      }

      setIsOrgTreeLoading(true);
      setOrgTreeError("");

      try {
        const cacheKeySuffix = user?.user?.username || "anonymous";
        const districtCacheKey = `districts_v2:${cacheKeySuffix}`;
        const subdistrictCacheKey = `subdistricts_v2:${cacheKeySuffix}`;

        const storedDistricts = localStorage.getItem(districtCacheKey);
        let districtRecords = storedDistricts ? JSON.parse(storedDistricts) : null;

        if (!Array.isArray(districtRecords) || districtRecords.length === 0) {
          const response = await axios.get(
            "/organisationUnits?level=3&paging=false&fields=id,displayName,parent[id,displayName]",
          );

          districtRecords = (response?.data?.organisationUnits || []).map((district) => ({
            id: district.id,
            name: district.displayName,
            regionId: district?.parent?.id || null,
            regionName: district?.parent?.displayName || "Unknown Region",
          }));

          localStorage.setItem(districtCacheKey, JSON.stringify(districtRecords));
        }

        const storedSubdistricts = localStorage.getItem(subdistrictCacheKey);
        let subdistrictRecords = storedSubdistricts ? JSON.parse(storedSubdistricts) : null;

        if (!Array.isArray(subdistrictRecords) || subdistrictRecords.length === 0) {
          try {
            const subdistrictResponse = await axios.get(
              "/organisationUnits?level=4&paging=false&fields=id,displayName,parent[id,displayName,parent[id,displayName]]",
            );

            subdistrictRecords = (subdistrictResponse?.data?.organisationUnits || [])
              .map((unit) => ({
                id: unit.id,
                name: unit.displayName,
                districtId: unit?.parent?.id || null,
                districtName: unit?.parent?.displayName || "Unknown District",
                regionId: unit?.parent?.parent?.id || null,
                regionName: unit?.parent?.parent?.displayName || "Unknown Region",
              }))
              .filter((record) => record.name && record.districtName);

            localStorage.setItem(subdistrictCacheKey, JSON.stringify(subdistrictRecords));
          } catch (subdistrictError) {
            console.warn("Unable to load level-4 org units (subdistricts):", subdistrictError);
            subdistrictRecords = [];
          }
        }

        const nextTree = buildOrgTreeFromBackend(districtRecords, subdistrictRecords, user);

        if (isMounted) {
          setOrgTree(nextTree);
          setOrgTreeError("");
        }
      } catch (error) {
        console.error("Failed to load org unit tree:", error);
        if (isMounted) {
          setOrgTree(buildFallbackTree(user));
          setOrgTreeError("Unable to load DHIS2 org units. Location tree unavailable.");
        }
      } finally {
        if (isMounted) {
          setIsOrgTreeLoading(false);
        }
      }
    };

    loadOrgUnits();

    return () => {
      isMounted = false;
    };
  }, [user?.user?.username]);

  const defaultDashboardLocation = useMemo(
    () => getHighestAccessibleLocation(orgTree),
    [orgTree],
  );

  const currentLocation = useMemo(() => {
    let resolvedLocation = orgTree;

    if (!routeState.isContextRoute) {
      resolvedLocation = orgTree;
    } else if (routeState.regionSlug) {
      const regionNode = findRegionBySlug(orgTree, routeState.regionSlug);

      if (regionNode) {
        resolvedLocation = regionNode;

        if (routeState.districtSlug) {
          const districtNode = findDistrictBySlug(regionNode, routeState.districtSlug);

          if (districtNode) {
            resolvedLocation = districtNode;

            if (routeState.subdistrictSlug) {
              resolvedLocation = findSubdistrictBySlug(districtNode, routeState.subdistrictSlug) || districtNode;
            }
          }
        }
      }
    }

    if (resolvedLocation?.hasAccess === false) {
      return defaultDashboardLocation || orgTree;
    }

    return resolvedLocation;
  }, [defaultDashboardLocation, orgTree, routeState]);

  const currentModuleSlug = useMemo(() => {
    const requestedModule = routeState.moduleSlug;

    if (!requestedModule || !CONTEXT_MODULES_BY_SLUG[requestedModule]) {
      return getDefaultModuleForLevel(
        currentLocation?.level || "national",
        user,
        assessmentUsers,
        currentLocation,
      );
    }

    if (
      isAssessmentUsersLoading
      && CONTEXT_MODULES_BY_SLUG[requestedModule].assessmentUsersOnly
    ) {
      return requestedModule;
    }

    if (!isModuleAllowedForLevel(
      requestedModule,
      currentLocation?.level || "national",
      user,
      assessmentUsers,
      currentLocation,
    )) {
      return getDefaultModuleForLevel(
        currentLocation?.level || "national",
        user,
        assessmentUsers,
        currentLocation,
      );
    }

    return requestedModule;
  }, [
    routeState.moduleSlug,
    currentLocation?.level,
    currentLocation?.id,
    user,
    assessmentUsers,
    isAssessmentUsersLoading,
  ]);

  const activePathIds = useMemo(
    () => buildActivePathIds(currentLocation),
    [currentLocation],
  );
  useEffect(() => {
    if (!routeState.isContextRoute) return;

    const hasScopedPath = Boolean(routeState.regionSlug || routeState.districtSlug || routeState.subdistrictSlug);
    if (isOrgTreeLoading) return;
    if (isAssessmentUsersLoading) return;
    if (hasScopedPath && orgTree.children.length === 0) return;

    const canonicalPath = buildContextPath(currentLocation, currentModuleSlug);

    if (routerLocation.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [
    routeState.isContextRoute,
    routeState.regionSlug,
    routeState.districtSlug,
    routeState.subdistrictSlug,
    isOrgTreeLoading,
    isAssessmentUsersLoading,
    currentLocation,
    currentModuleSlug,
    routerLocation.pathname,
    navigate,
  ]);

  const selectLocation = useCallback((nextLocation, preferredModuleSlug) => {
    if (!nextLocation || nextLocation.hasAccess === false) return false;

    const candidateModule = preferredModuleSlug || currentModuleSlug || "dashboard";
    const safeModule = isModuleAllowedForLevel(
      candidateModule,
      nextLocation.level,
      user,
      assessmentUsers,
      nextLocation,
    )
      ? candidateModule
      : getDefaultModuleForLevel(
        nextLocation.level,
        user,
        assessmentUsers,
        nextLocation,
      );

    navigate(buildContextPath(nextLocation, safeModule));
    return true;
  }, [assessmentUsers, currentModuleSlug, navigate, user]);

  const goToModule = useCallback((moduleSlug) => {
    const targetLocation =
      moduleSlug === "dashboard"
        ? currentLocation?.level === "national"
          ? currentLocation
          : defaultDashboardLocation || currentLocation || orgTree
        : currentLocation;

    if (!targetLocation) return false;

    if (!isModuleAllowedForLevel(
      moduleSlug,
      targetLocation.level,
      user,
      assessmentUsers,
      targetLocation,
    )) {
      return false;
    }

    navigate(buildContextPath(targetLocation, moduleSlug));
    return true;
  }, [
    assessmentUsers,
    currentLocation,
    defaultDashboardLocation,
    navigate,
    orgTree,
    user,
  ]);

  const contextValue = useMemo(
    () => ({
      orgTree,
      isOrgTreeLoading,
      orgTreeError,
      assessmentUsers,
      isAssessmentUsersLoading,
      isContextRoute: routeState.isContextRoute,
      currentLocation,
      currentModuleSlug,
      activePathIds,
      selectLocation,
      goToModule,
      buildContextPath,
    }),
    [
      orgTree,
      isOrgTreeLoading,
      orgTreeError,
      assessmentUsers,
      isAssessmentUsersLoading,
      routeState.isContextRoute,
      currentLocation,
      currentModuleSlug,
      activePathIds,
      selectLocation,
      goToModule,
    ],
  );

  return (
    <LocationContext.Provider value={contextValue}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationContext() {
  const context = useContext(LocationContext);

  if (!context) {
    throw new Error("useLocationContext must be used within a LocationProvider");
  }

  return context;
}
